import * as React from 'react'

import { observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  open: boolean
  initialName: string
  onSubmit: (newName: string) => void
  onCancel: () => void
  /** Required when nested in a focus-trapping ancestor (vaul Drawer) so Radix doesn't steal focus on every keystroke. */
  portalContainer?: React.RefObject<HTMLElement | null>
}

const RenameMapViewDialog: React.FC<IProps> = ({
  open,
  initialName,
  onSubmit,
  onCancel,
  portalContainer,
}) => {
  const [value, setValue] = React.useState(initialName)
  const inputRef = React.useRef<HTMLInputElement>(null)
  // Session-start snapshot — guards against external mid-session renames
  // mis-classifying the user's edit as unchanged.
  const sessionSeedRef = React.useRef(initialName)
  const prevOpenRef = React.useRef(false)

  // Seed only on false→true so an external rename mid-session doesn't overwrite the user's draft.
  React.useEffect(() => {
    const wasOpen = prevOpenRef.current
    prevOpenRef.current = open
    if (!open || wasOpen) return
    sessionSeedRef.current = initialName
    setValue(initialName)
    // Defer focus so dialog content is mounted before .focus()/.select().
    requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    })
  }, [open, initialName])

  const handleOpenChange = (next: boolean): void => {
    if (!next) onCancel()
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    const trimmed = value.trim()
    // Compare against the session-start seed (see sessionSeedRef declaration).
    if (!trimmed || trimmed === sessionSeedRef.current) {
      onCancel()
      return
    }
    onSubmit(trimmed)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={false} portalContainer={portalContainer}>
        <DialogHeader>
          <DialogTitle>{Localization.translator.renameMapView}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <label className="tw:block tw:text-label-md tw:text-foreground tw:mb-2">
            {Localization.translator.mapViewName}
            <input
              ref={inputRef}
              data-slot="rename-map-view-input"
              type="text"
              value={value}
              onChange={e => setValue(e.target.value)}
              className="tw:mt-1 tw:block tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-background tw:px-3 tw:py-2 tw:text-body-md tw:text-foreground tw:outline-none tw:focus-visible:border-ring tw:focus-visible:ring-2 tw:focus-visible:ring-ring/50"
            />
          </label>
          <DialogFooter>
            <Button
              variant="outline"
              size="lg"
              type="button"
              onClick={onCancel}
            >
              {Localization.translator.cancel}
            </Button>
            <Button variant="default" size="lg" type="submit">
              {Localization.translator.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default observer(RenameMapViewDialog)
